import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BusyIndicator, Button, FlexBox, MessageStrip, Text } from '@ui5/webcomponents-react';
import { api, type AttachmentFile } from '../api/client';

/**
 * Photos and files on a record, stored as SAP attachments (the same ones an SAP client user sees).
 *
 * On a phone "Take photo" opens the camera directly. Images are downscaled before upload — a phone photo is
 * several MB and field users are on mobile data, while ~1600px is plenty for a delivery note or a receipt.
 */
const MAX_EDGE = 1600, QUALITY = 0.82;

async function prepare(file: File): Promise<{ blob: Blob; name: string; type: string }> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return { blob: file, name: file.name, type: file.type || 'application/octet-stream' };
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1_000_000) return { blob: file, name: file.name, type: file.type };
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', QUALITY));
    if (!blob) return { blob: file, name: file.name, type: file.type };
    return { blob, name: file.name.replace(/\.[^.]+$/, '') + '.jpg', type: 'image/jpeg' };
  } catch { return { blob: file, name: file.name, type: file.type || 'application/octet-stream' }; }
}

export function AttachmentsPanel({ set, recordKey, canEdit }: { set: string; recordKey: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const camera = useRef<HTMLInputElement | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['attachments', set, recordKey], queryFn: () => api.attachments(set, recordKey) });

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true); setError(null);
    try {
      for (const file of Array.from(files)) {
        const { blob, name, type } = await prepare(file);
        await api.uploadAttachment(set, recordKey, blob, name, type);
      }
      await qc.invalidateQueries({ queryKey: ['attachments', set, recordKey] });
      qc.invalidateQueries({ queryKey: ['rec', set, recordKey] });   // the record now carries the attachment id
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  if (list.data && !list.data.supported) return null;    // entity has no attachment field — nothing to show
  const files = list.data?.files ?? [];

  return (
    <FlexBox direction="Column" style={{ gap: '0.75rem' }}>
      {error && <MessageStrip design="Negative" onClose={() => setError(null)}>{error}</MessageStrip>}
      {canEdit && (
        <FlexBox wrap="Wrap" alignItems="Center" style={{ gap: '0.5rem' }}>
          <Button icon="camera" design="Emphasized" disabled={busy} onClick={() => camera.current?.click()}>Take photo</Button>
          <Button icon="attachment" disabled={busy} onClick={() => picker.current?.click()}>Add file</Button>
          {busy && <><BusyIndicator active size="S" /><Text>Uploading…</Text></>}
          {/* capture= opens the camera on a phone; the second input is the gallery / file picker */}
          <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={e => { add(e.target.files); e.target.value = ''; }} />
          <input ref={picker} type="file" accept="image/*,application/pdf" multiple hidden onChange={e => { add(e.target.files); e.target.value = ''; }} />
        </FlexBox>
      )}
      {list.isLoading ? <BusyIndicator active /> : files.length === 0
        ? <Text style={{ color: 'var(--sapNeutralTextColor)' }}>No attachments yet.</Text>
        : <FlexBox wrap="Wrap" style={{ gap: '0.75rem' }}>{files.map(f => <Thumb key={`${f.line}-${f.name}`} set={set} recordKey={recordKey} file={f} />)}</FlexBox>}
    </FlexBox>
  );
}

/** One attachment: images show a thumbnail, everything else a download tile. Fetched with the auth header, so
 *  the bytes come back through the API and become a blob URL rather than a plain <img src>. */
function Thumb({ set, recordKey, file }: { set: string; recordKey: string; file: AttachmentFile }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file.isImage) return;
    let revoke: string | undefined;
    api.attachmentFile(set, recordKey, file.line)
      .then(blob => { revoke = URL.createObjectURL(blob); setUrl(revoke); })
      .catch(() => setUrl(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [set, recordKey, file.line, file.isImage]);

  const open = async () => {
    const blob = await api.attachmentFile(set, recordKey, file.line);
    const href = URL.createObjectURL(blob);
    window.open(href, '_blank');
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  };

  return (
    <FlexBox direction="Column" alignItems="Center" style={{ width: '8.5rem', gap: '0.25rem', cursor: 'pointer' }} onClick={open} title={file.name}>
      {file.isImage && url
        ? <img src={url} alt={file.name} style={{ width: '8.5rem', height: '8.5rem', objectFit: 'cover', borderRadius: '0.5rem', border: '1px solid var(--sapList_BorderColor,#e5e5e5)' }} />
        : <FlexBox justifyContent="Center" alignItems="Center" style={{ width: '8.5rem', height: '8.5rem', borderRadius: '0.5rem', background: 'var(--sapList_Background,#fff)', border: '1px solid var(--sapList_BorderColor,#e5e5e5)' }}>
            <Text style={{ fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)' }}>{file.extension.toUpperCase() || 'FILE'}</Text>
          </FlexBox>}
      <Text style={{ fontSize: '0.75rem', width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Text>
    </FlexBox>
  );
}
